export interface Route {
  path: string;
  component: string;
  title: string;
}

export interface RouteMatch {
  route: Route;
  params: Record<string, string>;
}

const ROUTES: Route[] = [
  { path: '/login', component: 'cp-page-login', title: 'Login' },
  { path: '/jarvis', component: 'cp-page-jarvis', title: 'Cockpit' },
  { path: '/board', component: 'cp-page-board', title: 'Dashboard / Control Board' },
  { path: '/approvals', component: 'cp-page-approvals', title: 'Freigaben' },
  { path: '/projects', component: 'cp-page-projects', title: 'Projekte' },
  { path: '/projects/:id', component: 'cp-page-projects', title: 'Projekt-Dashboard' },
  { path: '/projects/:id/specs/:specId', component: 'cp-page-projects', title: 'Spec-Detail' },
  { path: '/todos', component: 'cp-page-todos', title: 'Private To-Dos' },
  { path: '/todos/:pseudoProjectId', component: 'cp-page-todos', title: 'Private To-Dos' },
  { path: '/lifeplan', component: 'cp-page-lifeplan', title: 'Lebensplan' },
  { path: '/morning-check', component: 'cp-morning-check', title: 'Morning Check' },
  { path: '/messages', component: 'cp-page-messages', title: 'Nachrichten' },
  { path: '/ceo-chats', component: 'cp-page-ceo-chats', title: 'CEO Chats' },
  { path: '/cron', component: 'cp-page-cron', title: 'Routinen' },
  { path: '/team', component: 'cp-page-team', title: 'Team' },
  { path: '/breakdowns', component: 'cp-page-breakdowns', title: 'Goal Breakdown' },
  { path: '/inbox', component: 'cp-page-inbox', title: 'Inbox' },
  { path: '/signals', component: 'cp-page-signals', title: 'Signale' },
  { path: '/signal-sources', component: 'cp-page-signal-sources', title: 'Signal-Quellen' },
  { path: '/goals', component: 'cp-page-goals', title: 'Ziele' },
  { path: '/decisions', component: 'cp-page-decisions', title: 'Decisions' },
  { path: '/playbooks', component: 'cp-page-playbooks', title: 'Playbooks' },
  { path: '/settings', component: 'cp-page-settings', title: 'Settings' },
  { path: '/call/:skillId', component: 'cp-page-voice-call', title: 'Voice Call' },
  { path: '/chat', component: 'cp-page-chat', title: 'Chat' },
  { path: '/chat/:conversationId', component: 'cp-page-chat', title: 'Chat' },
  { path: '/people', component: 'cp-page-people', title: 'Personen' },
  { path: '/people/:id', component: 'cp-page-people', title: 'Person' },
  { path: '/agents', component: 'cp-page-agents', title: 'Agenten' },
  { path: '/agents/:id', component: 'cp-page-agents', title: 'Agent' },
  { path: '/companies', component: 'cp-page-companies', title: 'Firmen' },
  { path: '/companies/:id', component: 'cp-page-company', title: 'Firma' },
  { path: '/conversations', component: 'cp-page-conversations', title: 'Conversation Coach' },
  { path: '/conversations/:id', component: 'cp-page-conversations', title: 'Session' },
  { path: '/founder-board', component: 'cp-page-founder-board', title: 'Gruender Board' },
  { path: '/monitoring', component: 'cp-page-monitoring', title: 'System Monitoring' },
  { path: '/skills', component: 'cp-page-skills', title: 'Agent Skills' },
  { path: '/skills/promotion/:id', component: 'cp-page-skill-promotion', title: 'Skill bestätigen' },
  { path: '/memory-dashboard', component: 'cp-page-memory-dashboard', title: 'Memory Dashboard' },
];

const DEFAULT_PATH = '/jarvis';

function pathToRegex(path: string): { regex: RegExp; paramNames: string[] } {
  const paramNames: string[] = [];
  const pattern = path.replace(/:([^/]+)/g, (_match, paramName: string) => {
    paramNames.push(paramName);
    return '([^/]+)';
  });
  return { regex: new RegExp(`^${pattern}$`), paramNames };
}

function matchRoute(pathname: string): RouteMatch | undefined {
  for (const route of ROUTES) {
    const { regex, paramNames } = pathToRegex(route.path);
    const match = pathname.match(regex);
    if (match) {
      const params: Record<string, string> = {};
      paramNames.forEach((name, i) => {
        params[name] = match[i + 1];
      });
      return { route, params };
    }
  }
  return undefined;
}

class Router extends EventTarget {
  private _currentPath = '';
  private _currentParams: Record<string, string> = {};

  constructor() {
    super();
    window.addEventListener('hashchange', () => this._onHashChange());
    this._initRoute();
  }

  private _initRoute(): void {
    const hash = window.location.hash;
    if (!hash || hash === '#' || hash === '#/') {
      window.location.hash = `#${DEFAULT_PATH}`;
    } else {
      this._onHashChange();
    }
  }

  private _onHashChange(): void {
    const path = this._extractPath();
    if (path !== this._currentPath) {
      this._currentPath = path;
      const routeMatch = matchRoute(path);
      this._currentParams = routeMatch?.params ?? {};
      this.dispatchEvent(new CustomEvent('route-changed', {
        detail: { path, route: routeMatch?.route, params: this._currentParams },
      }));
    }
  }

  private _extractPath(): string {
    const hash = window.location.hash;
    return hash.startsWith('#') ? hash.slice(1) : '/board';
  }

  get currentPath(): string {
    return this._currentPath || this._extractPath();
  }

  getParams(): Record<string, string> {
    return { ...this._currentParams };
  }

  navigateTo(path: string): void {
    window.location.hash = `#${path}`;
  }

  getCurrentRoute(): Route | undefined {
    return matchRoute(this.currentPath)?.route;
  }

  getCurrentMatch(): RouteMatch | undefined {
    return matchRoute(this.currentPath);
  }

  getRoutes(): Route[] {
    return ROUTES;
  }
}

export const router = new Router();
export function navigateTo(path: string): void {
  router.navigateTo(path);
}
export function getCurrentRoute(): Route | undefined {
  return router.getCurrentRoute();
}
export function getParams(): Record<string, string> {
  return router.getParams();
}
